import { cinemaDayEnd, cinemaDayStart } from './cinema-day';
import { calendarDayEndInZone, calendarDayStartInZone, DEFAULT_TIME_ZONE, zonedDayKey } from './cinema-time-zone';
/**
 * Functions, not constants: a warm server instance evaluates this module once and would keep
 * serving the day it booted on, while the browser re-evaluates it on every page load. Server and
 * client then render different days, which both stales SSR output and breaks hydration (CIN-14426).
 */
export function dateToday(): Date {
    return new Date();
}

export function logicalStartOfToday(): Date {
    return cinemaDayStart(dateToday());
}

export const dateTomorrow = () => {
    const date = dateToday();
    date.setDate(date.getDate() + 1);
    return date;
};

export function dateTodayMinus(days = 0, hours = 0, minutes = 0, seconds = 0) {
    const date = new Date();
    if (days !== 0) {
        date.setDate(date.getDate() - days);
    }
    if (hours !== 0 || minutes !== 0 || seconds !== 0) {
        date.setTime(date.getTime() - (1000 * seconds + 1000 * 60 * minutes + 1000 * 60 * 60 * hours));
    }
    return date;
}

export function dateTodayPlus(days = 0, hours = 0, minutes = 0, seconds = 0) {
    const date = new Date();
    if (days !== 0) {
        date.setDate(date.getDate() + days);
    }
    if (hours !== 0 || minutes !== 0 || seconds !== 0) {
        date.setTime(date.getTime() + (1000 * seconds + 1000 * 60 * minutes + 1000 * 60 * 60 * hours));
    }
    return date;
}

export function datePlus(date: Date, days = 0, hours = 0, minutes = 0, seconds = 0) {
    const newDate = new Date(date);
    if (days !== 0) {
        newDate.setDate(newDate.getDate() + days);
    }
    if (hours !== 0 || minutes !== 0 || seconds !== 0) {
        newDate.setTime(newDate.getTime() + (1000 * seconds + 1000 * 60 * minutes + 1000 * 60 * 60 * hours));
    }
    return newDate;
}

export function setTime(date: Date, hours?: number, minutes?: number, seconds?: number, ms?: number): Date {
    const newDate = new Date(date);
    newDate.setHours(
        hours ?? date.getHours(),
        minutes ?? date.getMinutes(),
        seconds ?? date.getSeconds(),
        ms ?? date.getMilliseconds()
    );
    return newDate;
}

/**
 * Start of the Europe/Berlin calendar day `date` falls on. Zone-explicit rather than `setHours`, so
 * a UTC server and a Berlin browser compute the same instant and SSR matches hydration.
 */
export function setTimeMin(date: Date): Date {
    return calendarDayStartInZone(zonedDayKey(date, DEFAULT_TIME_ZONE), DEFAULT_TIME_ZONE);
}

/** End (23:59:59.999) of the Europe/Berlin calendar day `date` falls on; see {@link setTimeMin}. */
export function setTimeMax(date: Date): Date {
    return calendarDayEndInZone(zonedDayKey(date, DEFAULT_TIME_ZONE), DEFAULT_TIME_ZONE);
}

/**
 * Whether a string parses to a real date. Release dates originate from TMDB and are occasionally
 * malformed or empty, and `Intl` renders an unparseable value as the literal string "Invalid Date"
 * rather than throwing — so guard the value before handing it to a formatter.
 */
export function isValidDateString(date: string | null | undefined): date is string {
    return !!date && !isNaN(new Date(date).getTime());
}

export type DateTimeFormatOptions = Intl.DateTimeFormatOptions;

/** @deprecated Replaced with useFormat() */
export function formatDate(date: string | Date | undefined, locales?: string, options?: DateTimeFormatOptions): string {
    // `new Date(...)` always returns an object, so the old `if (tmpDate)` guard never failed and
    // the `| null` in the return type (and in every helper that funnels through here) was dead.
    const tmpDate: Date = new Date(date ?? '');
    // A missing or unparseable date renders empty; Intl throws on an invalid Date and takes the page down.
    if (Number.isNaN(tmpDate.getTime())) {
        return '';
    }
    return new Intl.DateTimeFormat(
        locales || 'de-DE',
        options || {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        }
    ).format(tmpDate);
}

/** @deprecated Replaced with useFormat() */
export function formatDay(date: string | Date, locales?: string, options?: DateTimeFormatOptions): string | null {
    if (date) {
        return formatDate(
            date,
            locales || 'de-DE',
            options || {
                weekday: 'short'
            }
        );
    }
    return null;
}

/** @deprecated Replaced with useFormat() */
export function formatDayNumeric(
    date: string | Date,
    locales?: string,
    options?: DateTimeFormatOptions
): string | null {
    if (date) {
        return formatDate(
            date,
            locales || 'de-DE',
            options || {
                day: 'numeric'
            }
        );
    }
    return null;
}

/** @deprecated Replaced with useFormat() */
export function formatMonthLong(date: string | Date, locales?: string, options?: DateTimeFormatOptions): string | null {
    if (date) {
        return formatDate(
            date,
            locales || 'de-DE',
            options || {
                month: 'long'
            }
        );
    }
    return null;
}

/** @deprecated Replaced with useFormat() */
export function formatYearNumeric(
    date: string | Date,
    locales?: string,
    options?: DateTimeFormatOptions
): string | null {
    if (date) {
        return formatDate(
            date,
            locales || 'de-DE',
            options || {
                year: 'numeric'
            }
        );
    }
    return null;
}

/** @deprecated Replaced with useFormat() */
export function formatTime(date: string | Date, locales?: string, options?: DateTimeFormatOptions): string {
    return formatDate(
        date,
        locales,
        options || {
            hour: 'numeric',
            minute: 'numeric'
        }
    );
}

/** @deprecated Replaced with useFormat() */
export function formatDateTime(date: string | Date, locales?: string, options?: DateTimeFormatOptions): string {
    return formatDate(
        date,
        locales,
        options || {
            weekday: 'short',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }
    );
}

/** @deprecated Replaced with useFormat() */
export function formatDateNumbersOnly(
    date: string | Date | undefined,
    locales?: string,
    options?: DateTimeFormatOptions
): string {
    return formatDate(
        date,
        locales,
        options || {
            day: 'numeric',
            month: 'numeric',
            year: 'numeric'
        }
    );
}

export function formatDateHyphenSeparatedYearMonthDay(date: string | Date): string {
    return formatDate_yyyyMMdd(date);
}

/** @deprecated Replaced with useFormat() */
export function formatDate_HHmm24(date: string | Date, locales?: string): string {
    return formatDate(date, locales, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });
}

/** @deprecated Replaced with useFormat() */
export function formatDateForMyRequestsList(date: string | Date, locales?: string): string {
    return formatDate(date, locales, {
        weekday: 'short',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    }).replace('.,', ',');
}

/** @deprecated Replaced with useFormat() */
export function formatDayMonth(date: string | Date, locales?: string): string {
    return formatDate(date, locales, {
        day: '2-digit',
        month: '2-digit'
    });
}

/** @deprecated Replaced with useFormat() */
export function formatDateWeekdayYearMonthDay(date: string | Date, locales?: string): string {
    return formatDate(date, locales, {
        weekday: 'short',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
    }).replace('.,', ',');
}

/** @deprecated Replaced with useFormat() */
export function formatDateWeekdayYearMonthDayHourMinute(date: string | Date, locales?: string): string {
    return formatDate(date, locales, {
        weekday: 'short',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    }).replace('.,', ',');
}

export function getAge(dateString: string | Date): number {
    const birthDate = new Date(dateString);
    const today = dateToday();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    return age;
}

/** @deprecated Use formatDate_yyyyMMddTHHmmssZ or formatDate_yyyyMMdd directly **/
export function createServerDate(value: string | Date) {
    const valueAsDateString = new Date(value);
    valueAsDateString.setUTCHours(0, 0, 0, 0);
    const isoString = valueAsDateString.toISOString();
    return isoString.replace('.000Z', 'Z');
}

export function formatDate_yyyyMMdd(value: Date | string): string {
    const date = new Date(value);
    const localDate = new Date(date.getTime());
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

export function formatDate_yyyyMMddTHHmmssZ(value: Date | string): string {
    const date = new Date(value);
    const isoString = date.toISOString();
    return isoString.substring(0, 19) + 'Z';
}

export const isDateFuture = (date: string | Date): boolean => {
    if (!date) {
        return false;
    }
    return new Date(date) > dateToday();
};

/* The logical day for a cinema starts at 4am and ends the next day at 3:59am!  */
/**
 * @deprecated "Logical Day" is the term CONTEXT.md tells us not to use. Import `cinemaDayStart` /
 * `cinemaDayEnd` from `./cinema-day` instead; these aliases exist only so the ~245 existing call
 * sites keep working while they are migrated.
 */
export { cinemaDayStart as getLogicalStartOfDay, cinemaDayEnd as getLogicalEndOfDay };

export function getWeekNumber(date: Date): number {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
}

/**
 * Returns an ISO 8601 year-week string for the LOCAL calendar day represented by
 * the input Date (matches the backend's `TO_CHAR(date, 'IYYY-"W"IW')` output).
 *
 * Examples: `"2026-W15"`, `"2023-W52"`.
 *
 * Note: uses `getFullYear/getMonth/getDate` (local-TZ) intentionally — this
 * mirrors the user's mental model when picking a date in a date-picker.
 */
export function getIsoYearWeek(date: Date): string {
    // Pin the LOCAL calendar day at UTC midnight so subsequent UTC arithmetic is
    // consistent and TZ-independent.
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    // Shift to the Thursday of that ISO week (ISO 8601 definition: week 1 is the
    // week containing the first Thursday of the ISO year).
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const isoYear = d.getUTCFullYear();
    const yearStart = new Date(Date.UTC(isoYear, 0, 1));
    const weekNo = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
    return `${isoYear}-W${String(weekNo).padStart(2, '0')}`;
}

/**
 * Extract the ISO week number from an ISO year-week string like `"2026-W15"` → `15`.
 */
export function parseIsoYearWeekNumber(weekKey: string): number {
    return parseInt(weekKey.split('-W')[1], 10);
}

export function getWeeksBetweenDates(
    startDate: Date,
    endDate: Date
): { weekNumber: number; weekKey: string; startDate: Date; endDate: Date }[] {
    const weeks: { weekNumber: number; weekKey: string; startDate: Date; endDate: Date }[] = [];

    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
        const weekStart = new Date(currentDate);
        // Derive BOTH weekKey and weekNumber from the ISO 8601 algorithm so
        // that display labels (weekNumber) and API matching keys (weekKey)
        // cannot diverge at year boundaries. Previously weekNumber used a
        // naive non-ISO algorithm which produced mismatches in late December /
        // early January.
        const weekKey = getIsoYearWeek(currentDate);
        const weekNumber = parseIsoYearWeekNumber(weekKey);
        currentDate.setDate(currentDate.getDate() + 6); // move to the end of the week
        const weekEnd = currentDate > endDate ? new Date(endDate) : new Date(currentDate);

        weeks.push({
            weekNumber,
            weekKey,
            startDate: weekStart,
            endDate: weekEnd
        });

        currentDate.setDate(currentDate.getDate() + 1); // move to the next week's start
    }

    return weeks;
}

export function getDateFourWeeksAgo() {
    const today = new Date();
    today.setDate(today.getDate() - 28);
    return today;
}

export function dateAsStringOrNull(date?: string | Date): string | null {
    return date ? formatDate_yyyyMMddTHHmmssZ(new Date(date)) : null;
}

export function getCinemaWeek(dateInput?: string | Date): {
    isTodayMonday: boolean;
    current: { monday: Date; start: Date; end: Date };
    next: { monday: Date; start: Date; end: Date };
} {
    const date = dateInput ? cinemaDayStart(new Date(dateInput)) : cinemaDayStart(new Date());
    const dayOfWeek = date.getDay();
    const isTodayMonday = dayOfWeek === 1;

    // Find the most recent Thursday
    const daysSinceThursday = (dayOfWeek + 3) % 7;
    const currentStart = new Date(date);
    currentStart.setDate(date.getDate() - daysSinceThursday);
    currentStart.setMilliseconds(0);
    const currentEnd = new Date(currentStart);
    currentEnd.setDate(currentStart.getDate() + 6);
    currentEnd.setMilliseconds(0);

    // Calculate the current and next Monday and Thursday
    const currentMonday = new Date(currentStart);
    currentMonday.setDate(currentStart.getDate() - 3);
    currentMonday.setMilliseconds(0);
    const nextStart = new Date(currentStart);
    nextStart.setDate(currentStart.getDate() + 7);
    nextStart.setMilliseconds(0);
    const nextEnd = new Date(nextStart);
    nextEnd.setDate(nextStart.getDate() + 6);
    nextEnd.setMilliseconds(0);
    const nextMonday = new Date(currentMonday);
    nextMonday.setDate(currentMonday.getDate() + 7);
    nextMonday.setMilliseconds(0);

    return {
        isTodayMonday,
        current: {
            monday: currentMonday,
            start: currentStart,
            end: currentEnd
        },
        next: {
            monday: nextMonday,
            start: nextStart,
            end: nextEnd
        }
    };
}

/**
 * Consecutive days starting at `startDate` (today when omitted).
 */
export function getDates(visibleDaysInFuture = 14, startDate?: Date): Date[] {
    return [...Array(visibleDaysInFuture)].map((_, index) => {
        const date = startDate ? new Date(startDate) : new Date();
        date.setDate(date.getDate() + index);
        return date;
    });
}

export const isDateToday = (date: string | Date): boolean => {
    if (!date) {
        return false;
    }
    const dateAsDate = new Date(date);
    const today = dateToday();

    return (
        dateAsDate.getFullYear() === today.getFullYear() &&
        dateAsDate.getMonth() === today.getMonth() &&
        dateAsDate.getDate() === today.getDate()
    );
};
